import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { devApi } from './devApi';

const PRIORITIES = [
  ['low', '低'], ['normal', '通常'], ['high', '高'], ['urgent', '至急'],
];

/**
 * 部品依頼を作成 — 発注書は作りません。資材の部品依頼（shizai_parts_request）として登録します。
 * 選んだ行の不足数（推奨発注数）をそのまま渡します。
 */
export const PartsRequestDialog = ({ devId, open, onOpenChange, lines, onCreated }) => {
  const [reason, setReason] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [priority, setPriority] = useState('normal');
  const [prototype, setPrototype] = useState('');
  const [category, setCategory] = useState('');
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) devApi.orderCategories().then((r) => setCats(r || [])).catch(() => setCats([]));
  }, [open]);

  const submit = async () => {
    if (!reason.trim()) { toast.error('理由を入力してください'); return; }
    setBusy(true);
    try {
      const r = await devApi.createPartsRequest(devId, {
        part_ids: lines.map((l) => l.id),
        needed_by: neededBy || null,
        reason: reason.trim(),
        priority,
        order_category_id: category || null,
        prototype_note: prototype.trim() || null,
      });
      // R4 · 購入品は REQ・自社製作は 製作依頼・在庫があるものは 出庫 に自動で分かれる
      const parts = [
        r.request_no ? `部品依頼 ${r.request_no}（${r.lines}行）` : null,
        (r.fabrications || []).length ? `製作依頼 ${(r.fabrications || []).map((f) => f.fab_no).join('・')}` : null,
        (r.issued || []).length ? `在庫から出庫 ${(r.issued || []).length}件` : null,
      ].filter(Boolean);
      toast.success(`手配しました: ${parts.join(' ／ ')}`, { duration: 12000 });
      setReason(''); setPrototype(''); setNeededBy('');
      onOpenChange(false);
      onCreated?.(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || '部品依頼を作成できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="dev-parts-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">部品依頼を作成</DialogTitle>
          <DialogDescription className="text-sm">
            発注書は作成しません。資材の「部品依頼」として登録され、担当者が調達します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-zinc-300" data-testid="dev-parts-request-lines">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-left text-xs text-zinc-600">
                <tr>
                  <th className="px-3 py-1.5">部品</th>
                  <th className="px-3 py-1.5">図番 / 版</th>
                  <th className="px-3 py-1.5 text-right">不足数</th>
                  <th className="px-3 py-1.5 text-right">依頼数</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {lines.map((l) => (
                  <tr key={l.id} data-testid={`dev-parts-request-line-${l.id}`}>
                    <td className="px-3 py-1.5">{l.name}</td>
                    <td className="px-3 py-1.5 text-xs text-zinc-600">
                      {l.drawing_no || '—'}{l.drawing_revision ? ` ${l.drawing_revision}` : ''}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.need}{l.uom}</td>
                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                      {l.recommended_order_qty}{l.uom}
                      {l.recommended_packs ? (
                        <span className="ml-1 text-xs font-normal text-zinc-600">
                          （{l.recommended_packs}パック）
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">理由（必須）</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="例: 温度試験機の試作1号機の製作に必要なため"
              data-testid="dev-parts-request-reason" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">希望納期</Label>
              <Input type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)}
                data-testid="dev-parts-request-needed-by" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">優先度</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="dev-parts-request-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PRIORITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">試作（任意）</Label>
              <Input value={prototype} onChange={(e) => setPrototype(e.target.value)}
                placeholder="例: 試作1号機" data-testid="dev-parts-request-prototype" />
            </div>
          </div>
          {cats.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-zinc-700">受注分類（任意・通知先の振り分けに使われます）</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="dev-parts-request-category">
                  <SelectValue placeholder="選択しない" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={submit} disabled={busy || lines.length === 0}
            data-testid="dev-parts-request-submit">
            {busy ? '作成中…' : `部品依頼を作成（${lines.length}行）`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
